import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { apiClient } from '../services';
import CampusPhoneVerification from '../components/CampusPhoneVerification';
import ErrorBoundary from '../components/ErrorBoundary';
import { completeSignup, encryptPassword, SignupRequest } from '../services/signupApi';

interface SignupFormValues {
  email: string;
  name: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  password: string;
  confirmPassword: string;
  userType: 'student' | 'doctor';
  university?: string;
  department?: string;
  licenseNumber?: string;
  idCardFile?: File; // 파일 객체 추가
}

interface ValidationErrors {
  email: string;
  password: string;
  confirmPassword: string;
}

interface ValidationSuccess {
  email: string;
  password: string;
  confirmPassword: string;
  phoneVerification: string;
}

// 본인확인 결과 타입 (실제 API 응답에 맞게 수정)
interface PhoneVerificationResult {
  code: number;
  status: string;
  message: string;
  data: {
    name: string;
    birth: string;   // YYYY-MM-DD 형식
    phone: string;
  };
}

// 의대생 대학교 목록
const UNIVERSITIES = [
  '가천대학교', '가톨릭대학교', '강원대학교', '건국대학교', '건양대학교', '경북대학교',
  '경상대학교', '경희대학교', '계명대학교', '고려대학교', '고신대학교', '가톨릭관동대학교',
  '단국대학교', '대구가톨릭대학교', '동국대학교', '동아대학교', '부산대학교', '서울대학교',
  '성균관대학교', '순천향대학교', '아주대학교', '연세대학교', '연세대학교 미래캠퍼스',
  '영남대학교', '울산대학교', '원광대학교', '을지대학교', '이화여자대학교', '인제대학교',
  '인하대학교', '전남대학교', '전북대학교', '제주대학교', '조선대학교', '차의과학대학교',
  '중앙대학교', '충남대학교', '충북대학교', '한림대학교', '한양대학교', '기타'
];

// 의사 진료과 목록
const DEPARTMENTS = [
  '일반진료', '가정의학과', '기초의학', '내과', '마취통증의학과', '방사선종양학과',
  '병리과', '비뇨의학과', '소아청소년과', '산부인과', '성형외과', '신경과', '신경외과',
  '심장혈관흉부외과', '안과', '영상의학과', '예방의학과', '응급의학과', '이비인후과',
  '임상약리학과', '외과', '재활의학과', '정신건강의학과', '정형외과', '직업환경의학과',
  '진단검사의학과', '피부과', '핵의학과', '기타'
];

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formValues, setFormValues] = useState<SignupFormValues>({
    email: '',
    name: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    password: '',
    confirmPassword: '',
    userType: 'student',
    university: '',
    department: '',
    licenseNumber: ''
  });

  const [dropdownStates, setDropdownStates] = useState({
    year: false,
    month: false,
    day: false,
    university: false,
    department: false
  });

  const [agreements, setAgreements] = useState({
    all: false,
    age: false,
    terms: false,
    privacy: false,
    marketing: false
  });

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [phoneVerificationData, setPhoneVerificationData] = useState<PhoneVerificationResult['data'] | null>(null); // 본인인증 데이터
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  // 유효성 검사 에러 상태
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    email: '',
    password: '',
    confirmPassword: ''
  });

  // 유효성 검사 성공 메시지 상태
  const [validationSuccess, setValidationSuccess] = useState<ValidationSuccess>({
    email: '',
    password: '',
    confirmPassword: '',
    phoneVerification: ''
  });

  // 약관 페이지에서 돌아왔을 때 또는 페이지 로드 시 폼 상태 복원
  useEffect(() => {
    // 먼저 React Router state에서 확인
    if (location.state?.formData) {
      const { 
        formValues: savedFormValues, 
        agreements: savedAgreements, 
        dropdownStates: savedDropdownStates,
        isEmailVerified: savedIsEmailVerified,
        isPhoneVerified: savedIsPhoneVerified,
        isImageUploaded: savedIsImageUploaded,
        uploadedFileName: savedUploadedFileName,
        validationErrors: savedValidationErrors,
        validationSuccess: savedValidationSuccess
      } = location.state.formData;
      
      if (savedFormValues) setFormValues(savedFormValues);
      if (savedAgreements) setAgreements(savedAgreements);
      if (savedDropdownStates) setDropdownStates(savedDropdownStates);
      if (savedIsEmailVerified !== undefined) setIsEmailVerified(savedIsEmailVerified);
      if (savedIsPhoneVerified !== undefined) setIsPhoneVerified(savedIsPhoneVerified);
      if (savedIsImageUploaded !== undefined) setIsImageUploaded(savedIsImageUploaded);
      if (savedUploadedFileName) setUploadedFileName(savedUploadedFileName);
      if (savedValidationErrors) setValidationErrors(savedValidationErrors);
      if (savedValidationSuccess) setValidationSuccess(savedValidationSuccess);
      
      // 복원 후 localStorage 삭제
      localStorage.removeItem('signupFormData');
    } 
    // React Router state가 없으면 localStorage에서 확인
    else {
      const savedData = localStorage.getItem('signupFormData');
      if (savedData) {
        try {
          const formData = JSON.parse(savedData);
          const { 
            formValues: savedFormValues, 
            agreements: savedAgreements, 
            dropdownStates: savedDropdownStates,
            isEmailVerified: savedIsEmailVerified,
            isPhoneVerified: savedIsPhoneVerified,
            isImageUploaded: savedIsImageUploaded,
            uploadedFileName: savedUploadedFileName,
            validationErrors: savedValidationErrors,
            validationSuccess: savedValidationSuccess
          } = formData;
          
          if (savedFormValues) setFormValues(savedFormValues);
          if (savedAgreements) setAgreements(savedAgreements);
          if (savedDropdownStates) setDropdownStates(savedDropdownStates);
          if (savedIsEmailVerified !== undefined) setIsEmailVerified(savedIsEmailVerified);
          if (savedIsPhoneVerified !== undefined) setIsPhoneVerified(savedIsPhoneVerified);
          if (savedIsImageUploaded !== undefined) setIsImageUploaded(savedIsImageUploaded);
          if (savedUploadedFileName) setUploadedFileName(savedUploadedFileName);
          if (savedValidationErrors) setValidationErrors(savedValidationErrors);
          if (savedValidationSuccess) setValidationSuccess(savedValidationSuccess);
          
          // 복원 후 localStorage 삭제
          localStorage.removeItem('signupFormData');
        } catch (error) {
          console.error('폼 데이터 복원 중 오류:', error);
          localStorage.removeItem('signupFormData');
        }
      }
    }
  }, [location.state]);

  // 이메일 유효성 검사 함수
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 비밀번호 유효성 검사 함수
  const validatePassword = (password: string): boolean => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,16}$/;
    return passwordRegex.test(password);
  };

  // 생년월일 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // 전체 동의 처리
  useEffect(() => {
    if (agreements.age && agreements.terms && agreements.privacy && agreements.marketing) {
      setAgreements(prev => ({ ...prev, all: true }));
    } else {
      setAgreements(prev => ({ ...prev, all: false }));
    }
  }, [agreements.age, agreements.terms, agreements.privacy, agreements.marketing]);

  // 실시간 유효성 검사
  useEffect(() => {
    // 이메일 유효성 검사
    if (formValues.email) {
      if (!validateEmail(formValues.email)) {
        setValidationErrors(prev => ({ ...prev, email: '올바른 이메일을 입력해주세요.' }));
        setValidationSuccess(prev => ({ ...prev, email: '' }));
      } else if (isEmailVerified) {
        setValidationErrors(prev => ({ ...prev, email: '' }));
        setValidationSuccess(prev => ({ ...prev, email: '사용 가능한 이메일입니다.' }));
      } else {
        setValidationErrors(prev => ({ ...prev, email: '' }));
        setValidationSuccess(prev => ({ ...prev, email: '' }));
      }
    } else {
      setValidationErrors(prev => ({ ...prev, email: '' }));
      setValidationSuccess(prev => ({ ...prev, email: '' }));
    }

    // 비밀번호 유효성 검사
    if (formValues.password) {
      if (!validatePassword(formValues.password)) {
        setValidationErrors(prev => ({ ...prev, password: '비밀번호 양식이 올바르지 않습니다.' }));
        setValidationSuccess(prev => ({ ...prev, password: '' }));
      } else {
        setValidationErrors(prev => ({ ...prev, password: '' }));
        setValidationSuccess(prev => ({ ...prev, password: '사용 가능한 비밀번호입니다.' }));
      }
    } else {
      setValidationErrors(prev => ({ ...prev, password: '' }));
      setValidationSuccess(prev => ({ ...prev, password: '' }));
    }

    // 비밀번호 확인 검사
    if (formValues.confirmPassword) {
      if (formValues.password !== formValues.confirmPassword) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: '비밀번호가 서로 일치하지 않습니다.' }));
        setValidationSuccess(prev => ({ ...prev, confirmPassword: '' }));
      } else if (validatePassword(formValues.password)) {
        setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
        setValidationSuccess(prev => ({ 
          ...prev, 
          password: '', // 비밀번호 성공 메시지 제거
          confirmPassword: '사용 가능한 비밀번호입니다.' 
        }));
      } else {
        setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
        setValidationSuccess(prev => ({ ...prev, confirmPassword: '' }));
      }
    } else {
      setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
      setValidationSuccess(prev => ({ ...prev, confirmPassword: '' }));
    }
  }, [formValues.email, formValues.password, formValues.confirmPassword, isEmailVerified]);

  // 휴대폰 인증 완료 감지 (메시지는 CampusPhoneVerification에서 표시)
  useEffect(() => {
    // CampusPhoneVerification 컴포넌트에서 자체적으로 메시지를 표시하므로 여기서는 처리하지 않음
  }, [isPhoneVerified]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleUserTypeChange = (type: 'student' | 'doctor') => {
    setFormValues(prev => ({ 
      ...prev, 
      userType: type,
      university: '',
      department: '',
      licenseNumber: ''
    }));
    setIsImageUploaded(false);
    setUploadedFileName('');
  };

  const handleDropdownToggle = (dropdown: keyof typeof dropdownStates) => {
    setDropdownStates(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  const handleDropdownSelect = (dropdown: keyof typeof dropdownStates, value: string) => {
    if (dropdown === 'year') {
      setFormValues(prev => ({ ...prev, birthYear: value }));
    } else if (dropdown === 'month') {
      setFormValues(prev => ({ ...prev, birthMonth: value }));
    } else if (dropdown === 'day') {
      setFormValues(prev => ({ ...prev, birthDay: value }));
    } else if (dropdown === 'university') {
      setFormValues(prev => ({ ...prev, university: value }));
    } else if (dropdown === 'department') {
      setFormValues(prev => ({ ...prev, department: value }));
    }
    
    setDropdownStates(prev => ({ ...prev, [dropdown]: false }));
  };

  const handleEmailVerification = async () => {
    console.log('이메일 중복 확인 버튼 클릭 - 입력된 이메일:', formValues.email);
    
    if (!formValues.email) {
      console.log('이메일이 입력되지 않음');
      setValidationErrors(prev => ({ ...prev, email: '이메일을 입력해주세요.' }));
      return;
    }
    
    if (!validateEmail(formValues.email)) {
      console.log('이메일 형식 유효성 검사 실패');
      setValidationErrors(prev => ({ ...prev, email: '올바른 이메일 형식을 입력해주세요.' }));
      return;
    }

    try {
      console.log('이메일 중복 확인 API 호출 시작');
      
      // API URL
      const url = `/api/auth/sign-up/check-email`;
      console.log('요청:', `${url}?email=${encodeURIComponent(formValues.email)}`);

      let result: any;
      try {
        const { data } = await apiClient.get(url, {
          params: { email: formValues.email },
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        });
        result = data;
      } catch (error: any) {
        if (error?.response?.status === 409) {
          console.log('이메일 이미 사용 중');
          setIsEmailVerified(false);
          setValidationErrors(prev => ({ ...prev, email: '이미 사용 중인 이메일입니다. 다른 이메일을 입력해주세요.' }));
          setValidationSuccess(prev => ({ ...prev, email: '' }));
          return;
        }
        if (error?.response) {
          console.error('HTTP 에러:', error.response.status, error.response.statusText);
          throw new Error(`HTTP error! status: ${error.response.status}`);
        }
        throw error;
      }
      console.log('응답 object:', result);
      
      if ((result.code === 200 || result.code === 1073741824) && result.data) {
        console.log('이메일 확인 성공 - 사용 가능 여부:', result.data.available);
        console.log('응답 상세:', {
          code: result.code,
          status: result.status,
          message: result.message,
          available: result.data.available
        });
        
        if (result.data.available) {
          console.log('최종 결과: 이메일 사용 가능');
          setIsEmailVerified(true);
          setValidationErrors(prev => ({ ...prev, email: '' }));
          setValidationSuccess(prev => ({ ...prev, email: '사용 가능한 이메일입니다.' }));
        } else {
          console.log('최종 결과: 이메일 이미 사용 중');
          setIsEmailVerified(false);
          setValidationErrors(prev => ({ ...prev, email: '이미 사용 중인 이메일입니다. 다른 이메일을 입력해주세요.' }));
          setValidationSuccess(prev => ({ ...prev, email: '' }));
        }
      } else {
        console.log('API 응답 에러:', result.message);
        setValidationErrors(prev => ({ ...prev, email: result.message || '이메일 확인 중 오류가 발생했습니다.' }));
        setValidationSuccess(prev => ({ ...prev, email: '' }));
      }
    } catch (error: any) {
      console.error('이메일 중복 확인 API 에러:', error);
      
      let errorMessage = '네트워크 오류가 발생했습니다.';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      setValidationErrors(prev => ({ ...prev, email: errorMessage }));
      setValidationSuccess(prev => ({ ...prev, email: '' }));
    }
  };

  // 휴대폰 본인확인 완료 처리 (Mobile-OK API 응답 직접 사용)
  const handlePhoneVerificationComplete = (result: any) => {
    console.log('휴대폰 인증 결과:', result);
    
    // Mobile-OK에서 이미 올바른 형식으로 결과가 오므로 직접 사용
    if (result.code === 200 && result.data) {
      setIsPhoneVerified(true);
      setPhoneVerificationData(result.data);
      
      // 인증된 데이터로 폼 자동 입력
      if (result.data.name) {
        setFormValues(prev => ({ ...prev, name: result.data.name }));
      }
      
      // 생년월일 파싱 및 자동 입력
      if (result.data.birth) {
        const [year, month, day] = result.data.birth.split('-');
        setFormValues(prev => ({ 
          ...prev, 
          birthYear: year,
          birthMonth: parseInt(month, 10).toString(),
          birthDay: parseInt(day, 10).toString()
        }));
      }
      
      console.log('본인인증 완료 - 데이터 자동 입력 완료');
    } else {
      setIsPhoneVerified(false);
      setPhoneVerificationData(null);
      
      console.error('휴대폰 인증 실패:', result.message);
    }
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // 파일 크기 체크 (5MB 제한)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          alert('파일 크기는 5MB 이하여야 합니다.');
          return;
        }
        
        setFormValues(prev => ({ ...prev, idCardFile: file }));
        setIsImageUploaded(true);
        setUploadedFileName(file.name);
      }
    };
    input.click();
  };

  const handleAllAgreementChange = () => {
    const newState = !agreements.all;
    setAgreements({
      all: newState,
      age: newState,
      terms: newState,
      privacy: newState,
      marketing: newState
    });
  };

  const handleAgreementChange = (key: keyof typeof agreements) => {
    setAgreements(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTermsClick = (type: 'terms' | 'privacy' | 'marketing') => {
    const urls = {
      terms: '/terms-of-service',
      privacy: '/privacy-policy',
      marketing: '/marketing-consent'
    };
    
    // localStorage에 폼 데이터 저장
    const formData = {
      formValues,
      agreements,
      dropdownStates,
      isEmailVerified,
      isPhoneVerified,
      isImageUploaded,
      uploadedFileName,
      validationErrors,
      validationSuccess
    };
    
    localStorage.setItem('signupFormData', JSON.stringify(formData));
    
    navigate(urls[type], { 
      state: { 
        returnTo: '/signup',
        formData
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== 회원가입 시작 ===');
    console.log('1. 폼 데이터 확인:', {
      email: formValues.email,
      name: formValues.name,
      userType: formValues.userType,
      university: formValues.university,
      department: formValues.department,
      licenseNumber: formValues.licenseNumber,
      birthYear: formValues.birthYear,
      birthMonth: formValues.birthMonth,
      birthDay: formValues.birthDay,
      password: '***masked***',
      confirmPassword: '***masked***',
      idCardFile: formValues.idCardFile ? '파일 있음' : '파일 없음'
    });
    
    console.log('2. 휴대폰 인증 데이터:', phoneVerificationData);
    console.log('3. 약관 동의 상태:', agreements);
    console.log('4. 유효성 검사 상태:', {
      isEmailVerified,
      isPhoneVerified,
      isFormValid: isFormValid()
    });
    
    try {
      // 비밀번호 RSA 암호화 (getPublicKey는 encryptPassword 함수 내부에서 호출됨)
      console.log('5. 비밀번호 암호화 중...');
      console.log('암호화할 비밀번호 길이:', formValues.password.length);
      const encryptedPassword = await encryptPassword(formValues.password);
      console.log('비밀번호 암호화 성공:', encryptedPassword ? '암호화 완료' : '암호화 실패');
      
      // 3. 새로운 API 명세에 맞게 데이터 구성
      const signupData: SignupRequest = {
        email: formValues.email,
        name: phoneVerificationData!.name,
        birth: phoneVerificationData!.birth,
        phone: phoneVerificationData!.phone,
        encryptedPassword,
        role: 'LEARNER' as const, // 수강자로 고정
        identity: formValues.userType === 'student' ? 'STUDENT' as const : 'DOCTOR' as const,
        department: formValues.userType === 'student' 
          ? formValues.university! 
          : formValues.department!,
        requiredAgreed: agreements.age && agreements.terms && agreements.privacy,
        marketingAgreed: agreements.marketing,
        ...(formValues.userType === 'doctor' && { 
          licenseNumber: formValues.licenseNumber! 
        }),
        ...(formValues.userType === 'student' && formValues.idCardFile && { 
          idCardFile: formValues.idCardFile 
        })
      };
      
      console.log('6. 최종 전송 데이터:', {
        ...signupData,
        encryptedPassword: '***encrypted***'
      });

      console.log('7. 회원가입 API 호출 시작');
      const result = await completeSignup(signupData);
      
      console.log('API 응답 전체:', result);
      console.log('응답 code:', result?.code);
      console.log('응답 status:', result?.status);
      console.log('응답 message:', result?.message);
      console.log('응답 data:', result?.data);
      
      // 성공 조건: code가 성공 코드이거나, data에 id가 있거나, 생성 메시지가 있는 경우
      const isSuccess = result?.code === 200 || 
                       result?.code === 1073741824 || 
                       result?.data?.id || 
                       result?.message?.includes('생성되었습니다') ||
                       result?.message?.includes('Person');
      
      if (isSuccess) {
        // 회원가입 완료 시 localStorage 정리
        localStorage.removeItem('signupFormData');
        console.log('회원가입 성공 - 페이지 이동:', result?.data);
        navigate('/signup-complete');
      } else {
        console.log('회원가입 실패 - 예상하지 못한 응답:', result);
        alert(result?.message || '회원가입에 실패했습니다.');
      }
      
    } catch (error: any) {
      console.error('회원가입 실패:', error);
      
      let errorMessage = '회원가입 중 오류가 발생했습니다.';
      
      if (error.message?.includes('암호화')) {
        errorMessage = '비밀번호 암호화에 실패했습니다. 다시 시도해주세요.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || '입력한 정보가 올바르지 않습니다.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message?.includes('공개키')) {
        errorMessage = '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      alert(errorMessage);
    }
  };

  const isFormValid = () => {
    const basicValidation = agreements.age && agreements.terms && agreements.privacy &&
                           validateEmail(formValues.email) && isEmailVerified && 
                           formValues.name && validatePassword(formValues.password) && 
                           formValues.password === formValues.confirmPassword &&
                           formValues.birthYear && formValues.birthMonth && formValues.birthDay &&
                           isPhoneVerified && phoneVerificationData;

    const userTypeValidation = formValues.userType === 'student' 
      ? (formValues.university && formValues.idCardFile)
      : (formValues.department && formValues.licenseNumber);
    
    const isValid = basicValidation && userTypeValidation;
    
    // 디버깅을 위한 로그 (개발 중에만 사용)
    if (process.env.NODE_ENV === 'development') {
      console.log('폼 유효성 검사:', {
        isValid,
        basicValidation: {
          agreements: { age: agreements.age, terms: agreements.terms, privacy: agreements.privacy },
          email: { valid: validateEmail(formValues.email), verified: isEmailVerified, value: formValues.email },
          name: !!formValues.name,
          password: { valid: validatePassword(formValues.password), match: formValues.password === formValues.confirmPassword },
          birth: { year: !!formValues.birthYear, month: !!formValues.birthMonth, day: !!formValues.birthDay },
          phone: { verified: isPhoneVerified, hasData: !!phoneVerificationData }
        },
        userTypeValidation: formValues.userType === 'student' 
          ? { university: !!formValues.university, idCardFile: !!formValues.idCardFile }
          : { department: !!formValues.department, licenseNumber: !!formValues.licenseNumber }
      });
    }
    
    return isValid;
  };

  return (
    <ErrorBoundary
      fallback={
        <Layout>
          <MainContainer>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <h2>회원가입 페이지 로딩 중 오류가 발생했습니다</h2>
              <p>페이지를 새로고침하거나 다시 시도해주세요.</p>
              <button onClick={() => window.location.reload()}>새로고침</button>
            </div>
          </MainContainer>
        </Layout>
      }
    >
      <Layout>
        <MainContainer>
          <Title>회원가입</Title>
          
          <FormContainer>
            <form onSubmit={handleSubmit}>
            {/* 이메일 */}
            <FormGroup>
              <FormLabel>이메일</FormLabel>
              <EmailInputGroup>
                <EmailInput
                  type="email"
                  name="email"
                  placeholder="이메일을 입력해주세요."
                  value={formValues.email}
                  onChange={handleInputChange}
                  disabled={isEmailVerified}
                  className={`${isEmailVerified ? 'verified' : ''} ${validationErrors.email ? 'error' : ''}`}
                />
                <EmailButton 
                  type="button"
                  onClick={handleEmailVerification}
                  disabled={isEmailVerified}
                >
                  중복 확인
                </EmailButton>
              </EmailInputGroup>
              {validationErrors.email && (
                <ErrorMessage>{validationErrors.email}</ErrorMessage>
              )}
              {validationSuccess.email && (
                <SuccessMessage>{validationSuccess.email}</SuccessMessage>
              )}
            </FormGroup>

            {/* 이름 */}
            <FormGroup>
              <FormLabel>이름</FormLabel>
              <Input
                type="text"
                name="name"
                placeholder="이름을 입력해주세요."
                value={formValues.name}
                onChange={handleInputChange}
                disabled={isPhoneVerified}
                className={isPhoneVerified ? 'verified' : ''}
              />
            </FormGroup>

            {/* 생년월일 */}
            <FormGroup>
              <FormLabel>생년월일</FormLabel>
              <BirthContainer>
                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('year')}
                    $isOpen={dropdownStates.year}
                    $hasValue={!!formValues.birthYear}
                    disabled={isPhoneVerified}
                  >
                    <span>{formValues.birthYear || '연'}</span>
                    <ChevronIcon $isOpen={dropdownStates.year}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.year && !isPhoneVerified && (
                    <DropdownMenu>
                      {years.map(year => (
                        <DropdownItem
                          key={year}
                          onClick={() => handleDropdownSelect('year', year.toString())}
                        >
                          {year}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>

                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('month')}
                    $isOpen={dropdownStates.month}
                    $hasValue={!!formValues.birthMonth}
                    disabled={isPhoneVerified}
                  >
                    <span>{formValues.birthMonth || '월'}</span>
                    <ChevronIcon $isOpen={dropdownStates.month}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.month && !isPhoneVerified && (
                    <DropdownMenu>
                      {months.map(month => (
                        <DropdownItem
                          key={month}
                          onClick={() => handleDropdownSelect('month', month.toString())}
                        >
                          {month}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>

                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('day')}
                    $isOpen={dropdownStates.day}
                    $hasValue={!!formValues.birthDay}
                    disabled={isPhoneVerified}
                  >
                    <span>{formValues.birthDay || '일'}</span>
                    <ChevronIcon $isOpen={dropdownStates.day}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.day && !isPhoneVerified && (
                    <DropdownMenu>
                      {days.map(day => (
                        <DropdownItem
                          key={day}
                          onClick={() => handleDropdownSelect('day', day.toString())}
                        >
                          {day}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>
              </BirthContainer>
            </FormGroup>

            {/* 휴대폰 본인인증 */}
            <FormGroup>
              <SectionTitle>휴대폰 본인인증</SectionTitle>
              <SectionDescription>본인 명의의 휴대폰으로 실명 인증을 하실 수 있습니다.</SectionDescription>
              <ErrorBoundary fallback={<div style={{ color: 'red' }}>본인인증 컴포넌트 로딩 오류</div>}>
                <CampusPhoneVerification 
                  name={formValues.name}
                  birthYear={formValues.birthYear}
                  birthMonth={formValues.birthMonth}
                  birthDay={formValues.birthDay}
                  onVerificationComplete={handlePhoneVerificationComplete}
                  disabled={isPhoneVerified}
                />
              </ErrorBoundary>
            </FormGroup>

            {/* 비밀번호 */}
            <FormGroup>
              <FormLabel>비밀번호</FormLabel>
              <Input
                type="password"
                name="password"
                placeholder="비밀번호를 입력해주세요."
                value={formValues.password}
                onChange={handleInputChange}
                className={validationErrors.password ? 'error' : ''}
              />
              {validationErrors.password && (
                <ErrorMessage>{validationErrors.password}</ErrorMessage>
              )}
              {validationSuccess.password && (
                <SuccessMessage>{validationSuccess.password}</SuccessMessage>
              )}
              
              <Input
                type="password"
                name="confirmPassword"
                placeholder="비밀번호를 다시 입력해주세요."
                value={formValues.confirmPassword}
                onChange={handleInputChange}
                className={validationErrors.confirmPassword ? 'error' : ''}
              />
              {validationErrors.confirmPassword && (
                <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>
              )}
              {validationSuccess.confirmPassword && (
                <SuccessMessage>{validationSuccess.confirmPassword}</SuccessMessage>
              )}
              
              <InputHelp>영문 대소문자, 숫자를 조합하여 8자 이상 16자 이하로 입력해주세요.</InputHelp>
            </FormGroup>

            {/* 회원 자격 확인 */}
            <FormGroup>
              <FormLabel>회원 자격 확인</FormLabel>
              <SectionDescription>
                회원 자격 확인을 위해 제출해주신 정보를 바탕으로 관리자 확인을 거쳐 회원가입이 최종 승인됩니다. 
                제출해주신 정보가 허위로 판명될 시 회원가입이 취소될 수 있습니다.
              </SectionDescription>
              
              <ToggleGroup>
                <ToggleButton
                  type="button"
                  className={formValues.userType === 'student' ? 'active' : ''}
                  onClick={() => handleUserTypeChange('student')}
                >
                  의대생
                </ToggleButton>
                <ToggleButton
                  type="button"
                  className={formValues.userType === 'doctor' ? 'active' : ''}
                  onClick={() => handleUserTypeChange('doctor')}
                >
                  의사
                </ToggleButton>
              </ToggleGroup>

              {formValues.userType === 'student' ? (
                <div>
                  <DropdownContainer>
                    <DropdownButton 
                      type="button"
                      onClick={() => handleDropdownToggle('university')}
                      $isOpen={dropdownStates.university}
                      $hasValue={!!formValues.university}
                    >
                      <span>{formValues.university || '소속 대학을 선택해주세요.'}</span>
                      <ChevronIcon $isOpen={dropdownStates.university}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ChevronIcon>
                    </DropdownButton>
                    {dropdownStates.university && (
                      <DropdownMenu>
                        {UNIVERSITIES.map(university => (
                          <DropdownItem
                            key={university}
                            onClick={() => handleDropdownSelect('university', university)}
                          >
                            {university}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    )}
                  </DropdownContainer>
                  
                  <UploadSection>
                    <UploadDescription>
                      {isImageUploaded ? uploadedFileName : '이름, 소속 대학, 학번이 식별 가능한 학생증 혹은 재학증명서 이미지를 업로드해주세요.'}
                    </UploadDescription>
                    <UploadButton 
                      type="button"
                      onClick={handleImageUpload}
                      disabled={isImageUploaded}
                    >
                      {isImageUploaded ? '업로드 완료' : '이미지 업로드'}
                    </UploadButton>
                  </UploadSection>
                </div>
              ) : (
                <div>
                  <MedicalDescription>
                    투비닥터 캠퍼스는 보건복지부 면허민원 서비스를 통해 면허번호 인증 절차를 진행합니다.
                  </MedicalDescription>
                  
                  <DropdownContainer>
                    <DropdownButton 
                      type="button"
                      onClick={() => handleDropdownToggle('department')}
                      $isOpen={dropdownStates.department}
                      $hasValue={!!formValues.department}
                    >
                      <span>{formValues.department || '진료과를 선택해주세요.'}</span>
                      <ChevronIcon $isOpen={dropdownStates.department}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ChevronIcon>
                    </DropdownButton>
                    {dropdownStates.department && (
                      <DropdownMenu>
                        {DEPARTMENTS.map(department => (
                          <DropdownItem
                            key={department}
                            onClick={() => handleDropdownSelect('department', department)}
                          >
                            {department}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    )}
                  </DropdownContainer>
                  <Temp8Div></Temp8Div>
                  <Input
                    type="text"
                    name="licenseNumber"
                    placeholder="면허번호를 입력해주세요."
                    value={formValues.licenseNumber || ''}
                    onChange={handleInputChange}
                  />
                </div>
              )}
            </FormGroup>

            {/* 약관 동의 */}
            <CheckboxGroup>
              <CheckboxWrapper>
                <CheckboxCustom checked={agreements.all} onClick={handleAllAgreementChange} />
                <CheckboxLabel className="bold" onClick={handleAllAgreementChange}>전체 동의</CheckboxLabel>
              </CheckboxWrapper>
              
              <Divider />
              
              <CheckboxWrapper>
                <CheckboxCustom checked={agreements.age} onClick={() => handleAgreementChange('age')} />
                <CheckboxLabel onClick={() => handleAgreementChange('age')}>[필수] 만 14세 이상입니다.</CheckboxLabel>
              </CheckboxWrapper>
              
              <CheckboxWrapper>
                <CheckboxCustom checked={agreements.terms} onClick={() => handleAgreementChange('terms')} />
                <CheckboxLabel onClick={() => handleAgreementChange('terms')}>[필수] 투비닥터 캠퍼스 이용약관 동의</CheckboxLabel>
                <ChevronRight onClick={() => handleTermsClick('terms')}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ChevronRight>
              </CheckboxWrapper>
              
              <CheckboxWrapper>
                <CheckboxCustom checked={agreements.privacy} onClick={() => handleAgreementChange('privacy')} />
                <CheckboxLabel onClick={() => handleAgreementChange('privacy')}>[필수] 투비닥터 캠퍼스 개인정보 수집 및 이용 동의</CheckboxLabel>
                <ChevronRight onClick={() => handleTermsClick('privacy')}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ChevronRight>
              </CheckboxWrapper>
              
              <CheckboxWrapper>
                <CheckboxCustom checked={agreements.marketing} onClick={() => handleAgreementChange('marketing')} />
                <CheckboxLabel onClick={() => handleAgreementChange('marketing')}>[선택] 마케팅 목적의 개인정보 수집 및 이용 동의</CheckboxLabel>
                <ChevronRight onClick={() => handleTermsClick('marketing')}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ChevronRight>
              </CheckboxWrapper>
            </CheckboxGroup>

            {/* 가입하기 버튼 */}
            <ButtonWrapper>
              <ActionButton
                type="submit"
                disabled={!isFormValid()}
              >
                가입하기
              </ActionButton>
            </ButtonWrapper>
          </form>
        </FormContainer>
      </MainContainer>
    </Layout>
  </ErrorBoundary>
);
};

const Temp8Div = styled.div`
  height: 8px;
`;

// 기존 스타일 유지하면서 피그마 디자인 적용
const MainContainer = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 128px ${({ theme }) => theme.layout.containerPaddingMobile} 160px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 100px 16px 120px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 80px 16px 100px;
  }
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 40px;
  line-height: 1.3em;
  margin-bottom: 28px;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 36px;
    margin-bottom: 40px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 32px;
    margin-bottom: 32px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 36px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 32px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 28px;
  }
`;

const FormGroup = styled.div`
  margin-top: 36px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FormLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.429em;
  color: rgba(46, 47, 51, 0.88);
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: rgba(46, 47, 51, 0.88);
  margin-bottom: 8px;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  line-height: 1.429em;
  color: rgba(55, 56, 60, 0.61);
  margin-bottom: 16px;
`;

const MedicalDescription = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: rgba(55, 56, 60, 0.61);
  margin-bottom: 10px;
  text-align: left;
`;

// 에러 및 성공 메시지
const ErrorMessage = styled.div`
  font-size: 12px;
  line-height: 1.334em;
  color: #FF4242;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  font-size: 12px;
  line-height: 1.334em;
  color: #00BF40;
  margin-top: 4px;
`;

// 이메일 입력 그룹
const EmailInputGroup = styled.div`
  display: flex;
  width: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px 0 0 12px;
  font-size: 16px;
  line-height: 1.5em;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:focus {
    border-color: #448181;
  }
  
  &:disabled {
    background-color: white !important;
    border-color: rgba(112, 115, 124, 0.16) !important;
  }
  
  &.verified {
    border-color: rgba(112, 115, 124, 0.16) !important;
    background-color: white !important;
  }
  
  &.error {
    border-color: rgba(255, 66, 66, 0.28);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: 12px;
    margin-bottom: 8px;
  }
`;

const EmailButton = styled.button`
  padding: 12px 16px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-left: none;
  border-radius: 0 12px 12px 0;
  background-color: transparent;
  font-weight: 600;
  font-size: 16px;
  color: #448181;
  cursor: pointer;
  white-space: nowrap;
  
  &:disabled {
    background-color: #F4F4F5;
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    border-radius: 12px;
    border-left: 1px solid rgba(112, 115, 124, 0.16);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.5em;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:focus {
    border-color: #448181;
  }
  
  &.verified {
    border-color: #448181;
    background-color: rgba(68, 129, 129, 0.05);
  }
  
  &.error {
    border-color: rgba(255, 66, 66, 0.28);
  }
  
  & + & {
    margin-top: 8px;
  }
`;

const InputHelp = styled.p`
  font-size: 12px;
  line-height: 1.334em;
  color: rgba(55, 56, 60, 0.61);
  margin-top: 4px;
`;

// 생년월일 드롭다운
const BirthContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const DropdownContainer = styled.div`
  flex: 1;
  position: relative;
`;

const DropdownButton = styled.button<{ $isOpen: boolean; $hasValue: boolean; disabled?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 12px;
  border: ${props => props.$isOpen ? '1px solid #448181' : '1px solid rgba(112, 115, 124, 0.16)'};
  border-radius: 12px;
  background: white;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  span {
    color: ${props => props.$hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
  
  &:disabled {
    background-color: rgba(68, 129, 129, 0.05);
    cursor: not-allowed;
  }
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
  color: rgba(55, 56, 60, 0.61);
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 56px;
  left: 0;
  right: 0;
  max-height: 400px;
  background: white;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.04);
  border-radius: 16px;
  border: 1px solid #EAEBEC;
  z-index: 10;
  overflow-y: auto;
  padding: 8px;
`;

const DropdownItem = styled.div`
  padding: 12px 0 12px 8px;
  color: #171719;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  cursor: pointer;
  border-radius: 12px;
  
  &:hover {
    background: rgba(23, 23, 25, 0.04);
  }
`;

const ActionButton = styled.button`
  height: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: #448181;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  padding: 16px 28px;
  transition: background-color 0.2s;
  
  &:disabled, &.disabled {
    background-color: #F4F4F5;
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
`;

// 회원 자격 확인 토글
const ToggleGroup = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
  position: relative;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  background-color: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 17px;
  font-weight: 500;
  color: rgba(55, 56, 60, 0.61);
  position: relative;
  z-index: 1;
  
  &:first-child {
    border-radius: 12px 0 0 12px;
  }
  
  &:last-child {
    border-radius: 0 12px 12px 0;
    margin-left: -1px;
  }
  
  &.active {
    border-color: #448181;
    color: #448181;
    background-color: rgba(68, 129, 129, 0.05);
    z-index: 2;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const UploadSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const UploadDescription = styled.div`
  flex: 1;
  color: ${props => props.children?.toString().startsWith('파일명') ? '#171719' : 'rgba(55, 56, 60, 0.61)'};
  font-size: 13px;
  line-height: 18px;
`;

const UploadButton = styled.button`
  padding: 12px 28px;
  background: transparent;
  border: 1px solid #448181;
  border-radius: 12px;
  color: #448181;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  
  &:disabled {
    background: #F4F4F5;
    border-color: rgba(112, 115, 124, 0.08);
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(112, 115, 124, 0.22);
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-wrap: wrap;
  }
`;

const CheckboxCustom = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1.5px solid rgba(112, 115, 124, 0.22);
  background-color: ${props => props.checked ? '#448181' : 'transparent'};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  cursor: pointer;
  
  ${props => props.checked && `
    border-color: #448181;
  `}
  
  /* 체크 표시 */
  &::after {
    content: '';
    display: ${props => props.checked ? 'block' : 'none'};
    width: 4px;
    height: 8px;
    border: 2px solid white;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
    margin-top: -2px;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 15px;
  line-height: 1.467em;
  color: rgba(46, 47, 51, 0.88);
  flex: 1;
  cursor: pointer;
  
  &.bold {
    font-weight: 600;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const ChevronRight = styled.div`
  color: #171719;
  cursor: pointer;
  margin-left: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-left: 28px;
    margin-top: 4px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 24px;
  width: 100%;
`;

export default Signup;